import Phaser from 'phaser';
import { setupCamera } from '../dpr.js';
import { AuthService } from '../services/AuthService';

export class LoginScene extends Phaser.Scene {
  private overlay: HTMLElement | null = null;

  constructor() {
    super({ key: 'LoginScene' });
  }

  async create(): Promise<void> {
    setupCamera(this, this.cameras.main, 'full');
    const data = this.scene.settings?.data as { kickReason?: string; resetBanner?: 'success' } | undefined;
    const kickReason = data?.kickReason;
    const resetBanner = data?.resetBanner;
    const auth = AuthService.getInstance();

    if (!auth.isLoggedIn()) {
      this.showLoginForm(kickReason, resetBanner);
      return;
    }

    // Refresh role from DB — in case admin approved while user was on pending screen
    await auth.refreshFromServer();

    if (auth.isApproved()) {
      this.scene.start('MenuScene');
    } else {
      this.showPendingScreen();
    }
  }

  private showLoginForm(kickReason?: string, resetBanner?: 'success'): void {
    const container = (document.getElementById('game-container') ?? document.body) as HTMLElement;
    this.overlay = document.createElement('div');
    this.overlay.setAttribute('data-login-overlay', '');
    this.overlay.style.cssText =
      'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#0a0806;z-index:9999;';

    if (kickReason === 'session-replaced') {
      const banner = document.createElement('div');
      banner.setAttribute('data-session-banner', '');
      banner.style.cssText =
        'background:#1a0f06;border:2px solid #c8973a;color:#c8973a;font-family:Georgia,serif;font-size:12px;padding:10px 18px;margin-bottom:12px;width:340px;box-sizing:border-box;display:flex;align-items:center;justify-content:space-between;gap:12px;';
      banner.innerHTML =
        '<span>Your account was connected from another device</span>' +
        '<button style="background:none;border:none;color:#c8973a;font-size:16px;cursor:pointer;padding:0;line-height:1;">✕</button>';
      banner.querySelector('button')!.addEventListener('click', () => banner.remove());
      this.overlay.appendChild(banner);
    }

    if (resetBanner === 'success') {
      const banner = document.createElement('div');
      banner.setAttribute('data-reset-banner', '');
      banner.style.cssText =
        'background:#0f1a0a;border:2px solid #7acc60;color:#7acc60;font-family:Georgia,serif;font-size:12px;padding:10px 18px;margin-bottom:12px;width:340px;box-sizing:border-box;text-align:center;';
      banner.textContent = '✓ Password updated — please sign in with your new password';
      this.overlay.appendChild(banner);
    }

    const loginBox = document.createElement('div');
    loginBox.innerHTML = `
      <div style="background:#0d0a0e;border:2px solid #5a3600;padding:28px 32px;width:340px;box-sizing:border-box;font-family:Georgia,serif;">
        <div style="text-align:center;color:#c8973a;font-size:15px;font-weight:bold;margin-bottom:18px;letter-spacing:1px;">✦  Enter Your Realm  ✦</div>
        <div id="ls-username-row" style="display:none;">
          <div style="color:#6b4020;font-size:10px;letter-spacing:1px;margin-bottom:5px;">USERNAME <span style="color:#5a3600;font-size:9px;">(required for registration)</span></div>
          <input id="ls-username" type="text" placeholder="Your pseudo" autocomplete="username"
            style="width:100%;box-sizing:border-box;background:#110e08;border:1px solid #5a3600;color:#c8973a;
                   padding:8px 12px;font-family:Georgia,serif;font-size:13px;outline:none;margin-bottom:14px;display:block;"/>
        </div>
        <div style="color:#6b4020;font-size:10px;letter-spacing:1px;margin-bottom:5px;">EMAIL</div>
        <input id="ls-email" type="email" placeholder="your@email.com" autocomplete="email"
          style="width:100%;box-sizing:border-box;background:#110e08;border:1px solid #5a3600;color:#c8973a;
                 padding:8px 12px;font-family:Georgia,serif;font-size:13px;outline:none;margin-bottom:14px;display:block;"/>
        <div style="color:#6b4020;font-size:10px;letter-spacing:1px;margin-bottom:5px;">PASSWORD</div>
        <input id="ls-pass" type="password" placeholder="••••••••" autocomplete="current-password"
          style="width:100%;box-sizing:border-box;background:#110e08;border:1px solid #5a3600;color:#c8973a;
                 padding:8px 12px;font-family:Georgia,serif;font-size:13px;outline:none;margin-bottom:10px;display:block;letter-spacing:3px;"/>
        <div id="ls-err" style="color:#c0392b;font-family:Georgia,serif;font-size:10px;min-height:14px;text-align:center;margin-bottom:12px;"></div>
        <div style="display:flex;gap:8px;justify-content:center;">
          <button id="ls-login" style="background:#1a0f06;border:1px solid #5a3600;color:#c8973a;font-family:Georgia,serif;font-size:12px;font-weight:bold;padding:8px 18px;cursor:pointer;">⚔ Login</button>
          <button id="ls-register" style="background:#1a0f06;border:1px solid #5a3600;color:#c8973a;font-family:Georgia,serif;font-size:12px;font-weight:bold;padding:8px 14px;cursor:pointer;">✦ Register</button>
        </div>
        <div style="text-align:center;margin-top:14px;">
          <button id="ls-forgot" style="background:none;border:none;color:#8a6a28;font-family:Georgia,serif;font-size:11px;cursor:pointer;text-decoration:underline;padding:4px 8px;">Forgot password?</button>
        </div>
        <div id="ls-forgot-panel" style="display:none;margin-top:14px;padding-top:14px;border-top:1px solid #3a2010;">
          <div style="color:#6b4020;font-size:10px;letter-spacing:1px;margin-bottom:5px;">EMAIL ADDRESS</div>
          <input id="ls-forgot-email" type="email" placeholder="your@email.com" autocomplete="email"
            style="width:100%;box-sizing:border-box;background:#110e08;border:1px solid #5a3600;color:#c8973a;padding:8px 12px;font-family:Georgia,serif;font-size:13px;outline:none;margin-bottom:10px;display:block;"/>
          <div id="ls-forgot-msg" style="color:#7acc60;font-family:Georgia,serif;font-size:11px;min-height:14px;text-align:center;margin-bottom:10px;"></div>
          <div style="display:flex;gap:8px;justify-content:center;">
            <button id="ls-forgot-send" style="background:#1a0f06;border:1px solid #5a3600;color:#c8973a;font-family:Georgia,serif;font-size:12px;padding:8px 14px;cursor:pointer;">Send reset link</button>
            <button id="ls-forgot-cancel" style="background:#1a0606;border:1px solid #5a1a00;color:#c05050;font-family:Georgia,serif;font-size:12px;padding:8px 14px;cursor:pointer;">Cancel</button>
          </div>
        </div>
      </div>`;
    this.overlay.appendChild(loginBox);

    container.appendChild(this.overlay);
    (this.overlay.querySelector('#ls-email') as HTMLInputElement)?.focus();

    const err = this.overlay.querySelector('#ls-err') as HTMLElement;
    const setError = (msg: string) => { if (err) err.textContent = msg; };
    const auth = AuthService.getInstance();

    const onAuthSuccess = () => {
      this.overlay?.remove();
      this.overlay = null;
      if (auth.isApproved()) {
        this.scene.start('MenuScene');
      } else {
        this.showPendingScreen();
      }
    };

    const usernameRow = this.overlay.querySelector('#ls-username-row') as HTMLElement;

    this.overlay.querySelector('#ls-login')!.addEventListener('click', async () => {
      if (usernameRow.style.display !== 'none') {
        usernameRow.style.display = 'none';
        setError('');
        return;
      }
      const email = (this.overlay!.querySelector('#ls-email') as HTMLInputElement).value.trim();
      const pass = (this.overlay!.querySelector('#ls-pass') as HTMLInputElement).value;
      if (!email || !pass) { setError('Email and password are required'); return; }
      try { await auth.login(email, pass); onAuthSuccess(); }
      catch (e: unknown) { setError(e instanceof Error ? e.message : 'An error occurred'); }
    });

    this.overlay.querySelector('#ls-register')!.addEventListener('click', async () => {
      if (usernameRow.style.display === 'none') {
        usernameRow.style.display = 'block';
        setError('');
        (this.overlay!.querySelector('#ls-username') as HTMLInputElement)?.focus();
        return;
      }
      const username = (this.overlay!.querySelector('#ls-username') as HTMLInputElement).value.trim();
      const email = (this.overlay!.querySelector('#ls-email') as HTMLInputElement).value.trim();
      const pass = (this.overlay!.querySelector('#ls-pass') as HTMLInputElement).value;
      if (!username || username.length < 2) { setError('Username is required (min 2 characters)'); return; }
      if (!email || !pass) { setError('Email and password are required'); return; }
      try { await auth.register(email, pass, username); onAuthSuccess(); }
      catch (e: unknown) { setError(e instanceof Error ? e.message : 'An error occurred'); }
    });

    // ── Forgot password flow ──
    const forgotPanel = this.overlay.querySelector('#ls-forgot-panel') as HTMLElement;
    const forgotMsg = this.overlay.querySelector('#ls-forgot-msg') as HTMLElement;
    const forgotEmail = this.overlay.querySelector('#ls-forgot-email') as HTMLInputElement;

    this.overlay.querySelector('#ls-forgot')!.addEventListener('click', () => {
      forgotPanel.style.display = 'block';
      forgotMsg.textContent = '';
      // Prefill with the email already typed for login, if any
      const typedEmail = (this.overlay!.querySelector('#ls-email') as HTMLInputElement).value.trim();
      if (typedEmail) forgotEmail.value = typedEmail;
      forgotEmail.focus();
    });

    this.overlay.querySelector('#ls-forgot-cancel')!.addEventListener('click', () => {
      forgotPanel.style.display = 'none';
      forgotMsg.textContent = '';
    });

    this.overlay.querySelector('#ls-forgot-send')!.addEventListener('click', async () => {
      const email = forgotEmail.value.trim();
      if (!email) {
        forgotMsg.style.color = '#c0392b';
        forgotMsg.textContent = 'Please enter your email';
        return;
      }
      try {
        await auth.forgotPassword(email);
        forgotMsg.style.color = '#7acc60';
        forgotMsg.textContent = "If an account matches, we've sent you an email. Check your spam folder.";
      } catch (e: unknown) {
        // forgotPassword always resolves per the no-enum design; this catch
        // is defensive (network error, etc.). Keep the same user-facing text.
        forgotMsg.style.color = '#7acc60';
        forgotMsg.textContent = "If an account matches, we've sent you an email. Check your spam folder.";
        void e;
      }
    });

    // Enter-key submit. Pressing Enter inside email / password fires the
    // primary action (Login by default, Register when the username row is
    // open). Username field also submits as Register since that's the only
    // mode it appears in. Forgot-password input fires the reset send.
    const emailInput = this.overlay.querySelector('#ls-email') as HTMLInputElement;
    const passInput  = this.overlay.querySelector('#ls-pass')  as HTMLInputElement;
    const userInput  = this.overlay.querySelector('#ls-username') as HTMLInputElement;
    const loginBtn   = this.overlay.querySelector('#ls-login')    as HTMLButtonElement;
    const regBtn     = this.overlay.querySelector('#ls-register') as HTMLButtonElement;
    const forgotSendBtn = this.overlay.querySelector('#ls-forgot-send') as HTMLButtonElement;

    const submitOnEnter = (input: HTMLInputElement, fallbackToRegister = false): void => {
      input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const inRegisterMode = usernameRow.style.display !== 'none';
        (inRegisterMode || fallbackToRegister ? regBtn : loginBtn).click();
      });
    };
    submitOnEnter(emailInput);
    submitOnEnter(passInput);
    submitOnEnter(userInput, /* fallbackToRegister */ true);

    forgotEmail.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      forgotSendBtn.click();
    });
  }

  private showPendingScreen(): void {
    const container = (document.getElementById('game-container') ?? document.body) as HTMLElement;
    const auth = AuthService.getInstance();
    this.overlay = document.createElement('div');
    this.overlay.setAttribute('data-pending-overlay', '');
    this.overlay.style.cssText =
      'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#0a0806;z-index:9999;';
    this.overlay.innerHTML = `
      <div style="background:#0d0a0e;border:2px solid #5a3600;padding:28px 32px;width:380px;box-sizing:border-box;font-family:Georgia,serif;text-align:center;">
        <div style="color:#c8973a;font-size:15px;font-weight:bold;margin-bottom:18px;letter-spacing:1px;">✦  Account Pending  ✦</div>
        <div style="color:#8a7040;font-family:Georgia,serif;font-size:13px;line-height:1.6;margin-bottom:22px;">
          Your account is awaiting approval.<br>Please wait for an admin to approve your access.
        </div>
        <div style="display:flex;gap:10px;justify-content:center;">
          <button id="ls-refresh" style="background:#1a0f06;border:1px solid #5a3600;color:#c8973a;font-family:Georgia,serif;font-size:12px;padding:8px 18px;cursor:pointer;">↺ Refresh</button>
          <button id="ls-logout" style="background:#1a0606;border:1px solid #5a1a00;color:#c05050;font-family:Georgia,serif;font-size:12px;padding:8px 18px;cursor:pointer;">⚔ Logout</button>
        </div>
      </div>`;

    container.appendChild(this.overlay);

    this.overlay.querySelector('#ls-refresh')!.addEventListener('click', async () => {
      await auth.refreshFromServer();
      if (auth.isApproved()) {
        this.overlay?.remove();
        this.overlay = null;
        this.scene.start('MenuScene');
      }
    });

    this.overlay.querySelector('#ls-logout')!.addEventListener('click', () => {
      auth.logout();
      this.overlay?.remove();
      this.overlay = null;
      this.showLoginForm();
    });
  }

  shutdown(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}
